import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import InputBox from "./InputBox";

const Container = styled.div`
	width: max-content;
	max-width: 17rem;
	background: white;
	border-radius: 0.5rem;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const InputGroup = styled.div`
	display: flex;
	gap: 0.5rem;
	margin-bottom: 0.5rem;
	position: relative;
`;

const AddButton = styled.button`
	display: flex;
	align-items: center;
	gap: 0.25rem;
	padding: 0.25rem 0.5rem;
	font-size: 0.875rem;
	color: white;
	background-color: #36395A;
	border: none;
	border-radius: 0.375rem;
	cursor: pointer;

	svg {
		width: 14px;
		height: 14px;
		stroke: currentColor;
		stroke-width: 2;
		stroke-linecap: round;
		stroke-linejoin: round;
		fill: none;
	}

	&:disabled {
		opacity: 0.5;
		pointer-events: none;
		cursor: not-allowed;
	}
`;

const NumberList = styled.div`
	display: flex;
	width: 100%;
	flex-wrap: wrap;
	gap: 0.25rem;
	margin-top: 1rem;
`;

const NumberItem = styled.div`
	display: flex;
	align-items: center;
	background-color: #f9fafb;
	border-radius: 0.375rem;
	font-size: 0.875rem;
`;

const NumberText = styled.span`
	font-family: monospace;
	padding: 0.125rem 0.5rem;
`;

const DeleteButton = styled.button`
	display: flex;
	align-items: center;
	padding: 0.125rem 0.25rem;
	color: #9ca3af;
	border: none;
	background: none;
	cursor: pointer;

	svg {
		width: 14px;
		height: 14px;
		stroke: currentColor;
		stroke-width: 2;
		stroke-linecap: round;
		stroke-linejoin: round;
		fill: none;
	}

	&:hover {
		color: #ef4444;
	}

	&:focus {
		outline: none;
	}
`;

const EmptyMessage = styled.div`
	color: #9ca3af;
	font-size: 0.875rem;
	padding: 0.25rem 0;
`;

const PlusIcon = () => (
	<svg viewBox="0 0 24 24">
		<line x1="12" y1="5" x2="12" y2="19" />
		<line x1="5" y1="12" x2="19" y2="12" />
	</svg>
);

const XIcon = () => (
	<svg viewBox="0 0 24 24">
		<line x1="18" y1="6" x2="6" y2="18" />
		<line x1="6" y1="6" x2="18" y2="18" />
	</svg>
);

const NumberListComponent = ({ numbers, setNumbers, placeholder }) => {
	const [inputValue, setInputValue] = useState("");
	const [error, setError] = useState("");

	const isDuplicate = (num) => numbers.includes(num);

	const handleAdd = () => {
		if (inputValue.trim() === "") {
			setError("Please enter a number");
			return;
		}

		const num = parseFloat(inputValue);
		if (isNaN(num)) {
			setError("Please enter a valid number");
			return;
		}

		if (isDuplicate(num)) {
			setError(`${num} is already in the list`);
			return;
		}

		setNumbers((prev) => [...prev, num]);
		setInputValue("");
		setError("");
	};

	const handleDelete = (index) => {
		setNumbers((prev) => prev.filter((_, i) => i !== index));
	};

	const handleKeyPress = (e) => {
		if (e.key === "Enter") {
			handleAdd();
		}
	};

	const handleInputChange = (e) => {
		setInputValue(e.target.value);
		setError(""); // Clear error when input changes
	};

	return (
		<Container>
			<InputGroup>
				<InputBox
					type="number"
					value={inputValue}
					onChange={handleInputChange}
					onKeyDown={handleKeyPress}
					placeholder={placeholder || "Enter a number"}
					error={!!error}
					errorMessage={error}
				/>
				<AddButton onClick={handleAdd} type="button" disabled={!inputValue.trim() || error}>
					<PlusIcon />
					Add
				</AddButton>
			</InputGroup>

			<NumberList>
				{numbers.map((num, index) => (
					<NumberItem key={`${num}-${index}`}>
						<NumberText>{num}</NumberText>
						<DeleteButton onClick={() => handleDelete(index)} aria-label="Delete number">
							<XIcon />
						</DeleteButton>
					</NumberItem>
				))}
			</NumberList>
		</Container>
	);
};

export default NumberListComponent;
